// SECURITY CENTER — cybersecurity & system health overview plus user
// session / device management (list + revoke). Additive module.
import React, { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, MonitorSmartphone, ShieldAlert, Activity, LockKeyhole, Ban } from "lucide-react";
import { AckReport } from "@/components/security/AckReport";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";

function Tile({ label, value, color = "#22D3EE", icon: Icon, testid }) {
  return (
    <div className="border border-[#2C2D35] rounded-sm p-3 flex items-center gap-3" data-testid={testid}>
      <Icon size={20} style={{ color }} />
      <div>
        <div className="font-display font-black text-2xl leading-none" style={{ color }}>{value}</div>
        <div className="tac-label mt-1">{label}</div>
      </div>
    </div>
  );
}

const HEALTH_COLOR = { operational: "#22C55E", ok: "#22C55E", degraded: "#F59E0B", down: "#EF4444", disabled: "#6B7280" };

export default function SecurityCenter() {
  const { user } = useAuth();
  const canRevoke = ["admin", "watch_commander"].includes(user?.role);
  const [ov, setOv] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [health, setHealth] = useState(null);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const load = useCallback(() => {
    client.get("/security/overview").then((r) => setOv(r.data)).catch(() => {});
    client.get("/security/sessions", { params: { active_only: activeOnly, q: q || undefined } }).then((r) => setSessions(r.data.sessions || [])).catch(() => {});
    client.get("/system/status").then((r) => setHealth(r.data)).catch(() => {});
  }, [activeOnly, q]);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);

  const revoke = async (s) => {
    if (!window.confirm(`Force sign-off ${s.name} (${s.device?.slice(0, 40)}…)? Their session ends immediately.`)) return;
    try { await client.post(`/security/sessions/${s.sid}/revoke`); toast.success(`Session revoked — ${s.name} signed off`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke session"); }
  };
  const revokeAll = async (s) => {
    if (!window.confirm(`Revoke ALL sessions for ${s.name}? Every device is signed off.`)) return;
    try { const { data } = await client.post(`/security/users/${s.user_id}/revoke-all`); toast.success(`${data.revoked} session(s) revoked for ${s.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke sessions"); }
  };
  const clearLock = async (l) => {
    try { await client.post("/security/lockouts/clear", { identifier: l.identifier }); toast.success(`Lockout cleared: ${l.identifier}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clear lockout"); }
  };

  const services = health?.services || [];

  return (
    <Layout>
      <PageHeader title="Security Center" sub="Cybersecurity & system health · user session & device management" />
      <div className="p-4 overflow-y-auto flex-1 space-y-4" data-testid="security-center">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Tile testid="sec-tile-active-sessions" label="Active Sessions" value={ov?.sessions?.active ?? "—"} color="#22C55E" icon={MonitorSmartphone} />
          <Tile testid="sec-tile-logins" label="Sign-ins (24h)" value={ov?.sessions?.logins_24h ?? "—"} color="#22D3EE" icon={ShieldCheck} />
          <Tile testid="sec-tile-accounts" label="User Accounts" value={ov?.accounts?.total ?? "—"} color="#3B82F6" icon={ShieldCheck} />
          <Tile testid="sec-tile-pending" label="Pending Approval" value={ov?.accounts?.pending_approval ?? "—"} color="#F59E0B" icon={LockKeyhole} />
          <Tile testid="sec-tile-locked" label="Locked Out Now" value={ov?.brute_force?.locked_now?.length ?? "—"} color="#EF4444" icon={Ban} />
          <Tile testid="sec-tile-denials" label="Access Denials (24h)" value={ov?.denials?.last_24h ?? "—"} color="#F97316" icon={ShieldAlert} />
        </div>

        {services.length > 0 && (
          <div className="border border-[#2C2D35] rounded-sm" data-testid="sec-health">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
              <Activity size={13} className="text-[#22C55E]" /> SYSTEM HEALTH
              {health?.overall && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded" style={{ background: HEALTH_COLOR[health.overall] || "#22C55E", color: "#0A0A0A" }}>{health.overall.toUpperCase()}</span>}
            </div>
            <div className="p-3 flex gap-2 flex-wrap">
              {services.map((s, i) => (
                <span key={i} className="px-2.5 py-1.5 rounded-sm border border-[#2C2D35] font-mono text-[10px] flex items-center gap-1.5" data-testid={`sec-health-${s.key || i}`}
                  title={s.detail || ""}>
                  <span className="w-2 h-2 rounded-full" style={{ background: HEALTH_COLOR[s.status] || "#22C55E" }} />
                  {(s.service || s.name || "service").toUpperCase()} · {(s.status || "ok").toUpperCase()}{Number.isFinite(s.latency_ms) ? ` · ${s.latency_ms}ms` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="border border-[#2C2D35] rounded-sm" data-testid="sec-sessions">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2 flex-wrap">
            <MonitorSmartphone size={13} className="text-[#22D3EE]" /> SESSIONS & DEVICES
            <input data-testid="sec-session-search" className={`${inp} py-1 w-52 ml-2`} placeholder="Search name / email / badge…" value={q} onChange={(e) => setQ(e.target.value)} />
            <button data-testid="sec-session-filter" onClick={() => setActiveOnly((v) => !v)}
              className="px-2 py-1 text-[9px] font-head font-bold rounded"
              style={activeOnly ? { background: "#22C55E", color: "#04120b" } : { border: "1px solid #2C2D35", color: "#9CA3AF" }}>
              {activeOnly ? "ACTIVE ONLY" : "ALL SESSIONS"}
            </button>
            {!canRevoke && <span className="font-mono text-[9px] text-[#6B7280] ml-auto">VIEW ONLY — revocation requires Watch Commander or Administrator</span>}
          </div>
          {sessions.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No sessions match.</div>}
          <div className="max-h-[420px] overflow-y-auto">
            {sessions.map((s) => (
              <div key={s.sid} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] text-[11px] flex-wrap" data-testid={`sec-session-${s.sid}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.revoked ? "#EF4444" : s.active ? "#22C55E" : "#6B7280" }} />
                <span className="text-white font-semibold w-40 truncate">{s.name}</span>
                <span className="font-mono text-[9px] px-1.5 py-0.5 rounded border border-[#2C2D35] text-[#9CA3AF]">{(s.role || "").replace(/_/g, " ").toUpperCase()}</span>
                <span className="font-mono text-[10px] text-[#9CA3AF] w-28 truncate">{s.ip || "—"}</span>
                <span className="font-mono text-[9px] text-[#6B7280] flex-1 min-w-[160px] truncate" title={s.device}>{s.device || "unknown device"}</span>
                <span className="font-mono text-[9px] text-[#6B7280]">seen {(s.last_seen || "").slice(5, 16).replace("T", " ")}</span>
                {s.revoked ? (
                  <span className="font-mono text-[9px] text-[#EF4444]">REVOKED · {s.revoked_by}</span>
                ) : canRevoke && (
                  <span className="flex gap-1.5">
                    <button data-testid={`sec-revoke-${s.sid}`} onClick={() => revoke(s)} className="px-2 py-1 text-[9px] font-head font-bold rounded text-white" style={{ background: "#DC2626" }}>REVOKE</button>
                    <button data-testid={`sec-revoke-all-${s.sid}`} onClick={() => revokeAll(s)} className="px-2 py-1 text-[9px] font-head font-bold rounded border border-[#7F1D1D] text-[#EF4444]">ALL DEVICES</button>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="border border-[#2C2D35] rounded-sm" data-testid="sec-lockouts">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
              <Ban size={13} className="text-[#EF4444]" /> BRUTE-FORCE LOCKOUTS ({ov?.brute_force?.locked_now?.length ?? 0} active · {ov?.brute_force?.tracked_identifiers ?? 0} tracked)
            </div>
            {(ov?.brute_force?.locked_now || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No accounts locked out right now.</div>}
            {(ov?.brute_force?.locked_now || []).map((l) => (
              <div key={l.identifier} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] text-[11px]">
                <span className="font-mono text-white flex-1 truncate">{l.identifier}</span>
                <span className="font-mono text-[9px] text-[#EF4444]">{l.count} fails · until {(l.locked_until || "").slice(11, 16)}</span>
                {canRevoke && <button data-testid={`sec-clear-lock-${l.identifier}`} onClick={() => clearLock(l)} className="px-2 py-1 text-[9px] font-head font-bold rounded text-black" style={{ background: "#22C55E" }}>CLEAR</button>}
              </div>
            ))}
          </div>
          <div className="border border-[#2C2D35] rounded-sm" data-testid="sec-denials">
            <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
              <ShieldAlert size={13} className="text-[#F97316]" /> RECENT ACCESS DENIALS
            </div>
            {(ov?.denials?.recent || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No blocked access attempts on file.</div>}
            <div className="max-h-64 overflow-y-auto">
              {(ov?.denials?.recent || []).map((d, i) => (
                <div key={i} className="px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[10px]">
                  <span className="text-[#6B7280]">{(d.ts || "").slice(5, 16).replace("T", " ")} · </span>
                  <span className="text-white">{d.user}</span>
                  <span className="text-[#F97316]"> [{d.action}] </span>
                  <span className="text-[#9CA3AF]">{d.detail || d.target}</span>
                </div>
              ))}
            </div>
            {(ov?.admin_actions || []).length > 0 && (
              <>
                <div className="px-3 py-1.5 border-y border-[#2C2D35] bg-[#0d0e11] font-head text-[10px] font-semibold text-[#9CA3AF]">SECURITY ADMIN ACTIONS</div>
                {(ov.admin_actions || []).slice(0, 8).map((a, i) => (
                  <div key={i} className="px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[10px]">
                    <span className="text-[#6B7280]">{(a.ts || "").slice(5, 16).replace("T", " ")} · </span>
                    <span className="text-white">{a.by}</span>
                    <span className="text-[#22D3EE]"> [{a.action}] </span>
                    <span className="text-[#9CA3AF]">{a.target} {a.detail ? `· ${a.detail}` : ""}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <AckReport />
      </div>
    </Layout>
  );
}
